import { createSelector } from 'reselect';
import { Location } from 'react-router-dom';
import { FILTER_JACKPOT, LIVE_CASINO_PATHS } from '~/components/router/casino-routes';
import { EnhancedGame } from '~/components/casino-shared/types';
import type { CasinoSharedRootState } from '~/components/casino-shared/ducks/types';

const REDUCER_NAME = 'allGames';

// [REDUCER_NAME]? , a question mark here is for search component
// if casino, or liveCasino, or allGames undefined in the store to not crash app
// some of them can be empty because we use explore page with categories, and without
// games without categories store in allGames reducer

const gamesByGameCodeSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME]?.games;
const hydratedSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME]?.hydrated;
const loadingSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME]?.loading;
const gameNamesByIdxSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME]?.gameNamesByIdx;
const gameCodesByProviderCodeSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME]?.gameCodesByProviderCode;
const gameCodesBySubProviderCodeSelector = (state: CasinoSharedRootState) =>
	state[REDUCER_NAME]?.gameCodesBySubProviderCode;
const providersSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME]?.providers;
const subProvidersSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME]?.subProviders;
const gameCodesByIdxSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME]?.gameCodesByIdx;
const gameIdxByFilterSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME]?.gameIdxByFilter;
const gameIdxByProviderSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME]?.gameIdxByProvider;

const gamesByIndexSelector = createSelector(
	[gamesByGameCodeSelector, gameCodesByIdxSelector],
	(gamesByGameCode, gameCodes) => {
		if (!gameCodes) return {};
		return gameCodes.reduce<Record<number, EnhancedGame>>((acc, gameCode, idx) => {
			acc[idx] = gamesByGameCode[gameCode];
			return acc;
		}, {});
	}
);

const gamesSelector = createSelector([gamesByGameCodeSelector], gamesByGameCode => {
	if (!gamesByGameCode) return [];
	return Object.values(gamesByGameCode);
});

const gamesByNameSelector = createSelector(
	[gameNamesByIdxSelector, gameCodesByIdxSelector, gamesByGameCodeSelector],
	(gameNames, gameCodes, gamesByGameCode) => {
		if (!gameCodes) return {};
		return gameCodes.reduce<Record<string, EnhancedGame>>((acc, gameCode, idx) => {
			acc[gameNames[idx]] = gamesByGameCode[gameCode];
			return acc;
		}, {});
	}
);

export const FILTER_MAPPINGS = {
	[FILTER_JACKPOT]: 'jackpot'
};

export const filterSelector = (_: unknown, props: { filter: string }) => props.filter;
export const isLiveCasinoLocationSelector = (_: unknown, props: { location: Location }) =>
	LIVE_CASINO_PATHS.some(path => props.location.pathname.includes(path));

export const filteredGamesSelector = createSelector(
	[
		gamesByGameCodeSelector,
		gameCodesByIdxSelector,
		gameIdxByFilterSelector,
		hydratedSelector,
		filterSelector,
		isLiveCasinoLocationSelector
	],
	(gamesByGameCode, gameCodesByIdx, gameIdxByFilter, hydrated, filter, isLiveCasinoLocation) => {
		let filteredGames: EnhancedGame[] = [];
		if (!hydrated) return filteredGames;
		if (!filter) {
			filteredGames = Object.values(gamesByGameCode);
			if (isLiveCasinoLocation) {
				filteredGames = filteredGames.filter(game => game.categoryCode === 'CASINO_LIVE_CASINO');
			}
			return filteredGames;
		}
		const activeFilter = FILTER_MAPPINGS[filter as keyof typeof FILTER_MAPPINGS];
		if (!activeFilter || !gameIdxByFilter[activeFilter as keyof typeof gameIdxByFilter]) {
			return [];
		}
		filteredGames =
			gameIdxByFilter[activeFilter as keyof typeof gameIdxByFilter]?.map(
				gameIdx => gamesByGameCode[gameCodesByIdx[gameIdx]]
			) ?? [];

		if (isLiveCasinoLocation) {
			filteredGames = filteredGames.filter(game => game.categoryCode === 'CASINO_LIVE_CASINO');
		}

		return filteredGames;
	}
);

export {
	gamesSelector,
	gamesByIndexSelector,
	gamesByGameCodeSelector,
	gameIdxByFilterSelector,
	gameCodesByIdxSelector,
	hydratedSelector,
	gameNamesByIdxSelector,
	gameIdxByProviderSelector,
	gamesByNameSelector,
	loadingSelector,
	gameCodesByProviderCodeSelector,
	gameCodesBySubProviderCodeSelector,
	providersSelector,
	subProvidersSelector
};
